<template>
  <v-container
    class="pa-0 ma-0"
    tabindex="-1"
  >
    <div class="calendar-sidebar-header pt-3 pb-2 px-4">
      <v-btn
        color="primary"
        icon
        @click="previousMonths"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <v-btn
        color="primary"
        width="160"
        outlined
        small
        @click="goToToday"
      >
        {{ $t('label.today') }}
      </v-btn>
      <v-btn
        color="primary"
        icon
        @click="nextMonths"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
    <vc-date-picker
      ref="picker"
      v-model="date"
      tabindex="-1"
      is-inline
      is-required
      :first-day-of-week="$localized.firstDayOfWeek() + 1"
      :locale="$i18n.locale"
      color="blue"
      :attributes="attributes"
      :rows="3"
      :theme="theme"
      :is-dark="$vuetify.theme.dark"
      is-expanded
      @dayclick="dayclick"
      @update:from-page="updateFromPage"
    />
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue';
import VCalendar from 'v-calendar';
import { performAction } from '@/services/vue-utils';
import {
  dateByAddingDays, dateByAddingMonths, dateIndexFromDate, isDateBeforeDate,
} from '@/services/time-utils';
import DateSlot from '@/model/DateSlot';
import { isOpenFromHours } from '@/services/timestlot-utils';

Vue.use(VCalendar, {
  componentPrefix: 'vc',
});

export default Vue.extend({
  name: 'Calendar',
  props: { },
  data() {
    return {
      date: this.$tstore.state.update.date,
      theme: {
        container: {
          light: 'light-theme',
          dark: 'vc-text-white vc-rounded',
        },
        weekdays: {
          light: 'vc-text-xs text--secondary',
          dark: 'vc-text-xs text--secondary',
        },
      },
      fromPage: { month: 0, year: 0 } as { month: number, year: number },
    };
  },
  computed: {
    currentDate(): Date {
      return this.$tstore.state.update.date;
    },
    dates(): Date[] {
      const dates: Date[] = [];

      let startDate = new Date(this.fromPage.year, this.fromPage.month - 1, 1);
      const endDate = dateByAddingMonths(startDate, 3);

      const nowDate = this.$tstore.getters.nowDate as Date;
      if (isDateBeforeDate(startDate, nowDate)) startDate = nowDate;

      for (let date = startDate; isDateBeforeDate(date, endDate); date = dateByAddingDays(date, 1)) {
        dates.push(date);
      }
      return dates;
    },
    dateSlots(): DateSlot[] {
      const { dateSlotsByDateIndex } = this.$tstore.state.reservations;
      return this.dates.flatMap((d) => dateSlotsByDateIndex[dateIndexFromDate(d)] ?? []);
    },
    attributes(): any[] {
      const now = this.$tstore.getters.nowDate as Date;
      const confirmedDates = this.dateSlots.flatMap((ds) => (ds.confirmedCount > 0 ? ds.date : []));
      const nonConfirmedDates = this.dateSlots.flatMap((ds) => (ds.nonConfirmedCount > 0 ? ds.date : []));
      const closedDates = this.dates.flatMap((d) => (!isOpenFromHours(
        d,
        this.$tstore.getters.openingHoursInWeekdaysByTimeIndex,

        this.$tstore.getters.datesAndTimesById,
      ) ? d : []));

      return [
        { // confirmed + planned
          dot: {
            color: 'blue',
          },
          dates: confirmedDates,
        },
        { // unconfirmed
          dot: {
            color: 'purple',
          },
          dates: nonConfirmedDates,
        },
        { // closed
          highlight: {
            contentClass: 'closed-day',
          },
          dates: closedDates,
          order: 1,
        },
        { // today
          highlight: {
            color: 'primary',
            fillMode: 'none',
            class: 'highlight--today',
            contentClass: 'today',
          },
          dates: [
            now,
          ],
        },
      ];
    },
  },
  watch: {
    currentDate() {
      this.date = this.$tstore.state.update.date;
      const calendar = (this.$refs.picker as Vue).$refs.calendar as any;
      calendar.move(this.$tstore.state.update.date);
    },
  },
  methods: {
    async goToDate(date: Date) {
      // ensure calendar-friendly view is pushed when selecting a date from
      // other places like settings, etc.
      if (this.$router.currentRoute.meta?.hasDatePicker !== true) {
        this.$router.push('/guests');
      }

      this.$emit('change', date);
      await performAction(
        null,
        this.$i18n.tc('error.load_data'),
        async () => this.$tstore.dispatch('changeToDate', date),
      );
    },
    async dayclick(event: { date: Date }) {
      (document.activeElement as HTMLElement | null)?.blur();
      this.goToDate(event.date);
    },
    goToToday() {
      this.goToDate(new Date());
      const calendar = (this.$refs.picker as Vue).$refs.calendar as any;
      calendar.move(new Date(), { position: 1 });
    },
    previousMonths() {
      const calendar = (this.$refs.picker as Vue).$refs.calendar as any;
      calendar.move(-1);
    },
    nextMonths() {
      const calendar = (this.$refs.picker as Vue).$refs.calendar as any;
      calendar.move(1);
    },
    updateFromPage(page: { month: number, year: number }) {
      this.fromPage = page;
    },
  },
});
</script>

<style lang="scss">

  .calendar-sidebar-header {
    margin-left: -12px;
    margin-right: -12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    -webkit-box-align: center
  }

  .vc-container {
    font-family: $body-font-family !important;
    background-color: transparent !important;
    border: 1px solid transparent !important;
    --primary-700: #35f !important;

    &.vc-is-dark {
      background-color: transparent !important;
      border: 1px solid transparent !important;
      --primary-700: #fff !important;
    }
  }

  .vc-day-content:focus {
    background-color: transparent !important;
  }

  .vc-arrows-container {
    display: none !important;
  }

  .vc-weekday {
    font-weight: normal !important;
    font-size: 12px!important;

    .vc-is-dark & {
      color: var(--gray-500) !important;
    }
  }

  .is-today :not(.highlight--today).vc-highlight {
    width: calc(24px - 6px) !important;
    height: calc(24px - 6px) !important;
  }
  .vc-highlight {
    width: 24px !important;
    height: 24px !important;
  }

  .vc-weeks {
    padding: 4px !important;
  }

  .vc-day {
    min-height: 38px !important;
  }

  .vc-nav-popover-container {
    background-color: $grey-90-color !important;

    .vc-is-dark & {
      color: white  !important;
    }
  }

  .vc-nav-title:hover {
    .vc-is-dark & {
      color: $grey-90-color  !important;
    }
  }

  .vc-nav-item.is-active {
    color: white  !important;
  }

  .vc-nav-title.vc-grid-focus {
    color: white  !important;
  }

  .vc-nav-item.is-current {
    border-color: transparent !important;
  }

  .vc-nav-item:focus {
    border-color: transparent !important;
  }

  .vc-blue {
    --blue-200: #35f!important;
    --blue-300: #35f!important;
    --blue-100: #35f!important;
    --blue-400: #35f!important;
    --blue-500: #35f!important;
    --blue-600: #35f!important;
    --blue-700: #35f!important;
    --blue-800: #35f!important;
    --blue-900: #35f!important;
  }

  .vc-dot,.vc-bar {
    --purple-600: #E42DD0!important;
    --blue-600: #35f!important;
    --purple-500: #E42DD0!important;
    --blue-500: #35f!important;
  }

  .closed-day {
    color: $grey-22-color !important;

    .vc-is-dark & {
      color: $grey-50-color !important;
    }

  }

</style>
